/* eslint-disable react-hooks/exhaustive-deps */
import { Box, ButtonProps, useMediaQuery, useTheme } from "@mui/material";
import { GridAddIcon, GridDeleteIcon } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ReactComponent as Export } from "../../../assets/icons/export.svg";
import { COLORS } from "../../../assets/theme";
import CombinedSection from "../../../components/CombinedSection";
import Actions from "../../../components/common/Actions";
import CustomDateRangePicker from "../../../components/common/CustomDateRangePicker";
import CustomDialog from "../../../components/common/CustomDialog";
import CustomLabel from "../../../components/common/CustomLabel";
import SearchForm from "../../../components/common/SearchForm";
import CustomTable from "../../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../../components/common/table/SearchHeaderContainer";
import TestingSection from "../../../components/TestFields";
import ToastNotification from "../../../components/ToastNotification";
import {
  addContentTranslationApi,
  ContentHistory,
  ContentTranslationCreateAllApi,
  ContentTranslationType,
  deleteContentTranslationApi,
  editContentTranslationApi,
  exportContentTranslation,
  fetchContentHistory,
  getContentTranslationById,
  useContentTranslationQuery,
} from "../../../services/ContentTranslationService";
import {
  fetchCountry,
  useCountriesQuery,
} from "../../../services/CountryService";
import { fetchOperator } from "../../../services/OperatorsService";
import {
  fetchProvider,
  useProviderOptionQuery,
} from "../../../services/ProviderService";
import {
  fetchRoutingPlan,
  useRoutingPlansQuery,
} from "../../../services/RoutingPlanService";
import {
  DateRange,
  IField,
  Option,
  Params,
} from "../../../types/table/interfaces.type";
import { loadOperatorsByCountry } from "../../../utils/commonDataUtils";
import History from "../../../components/History";
import RelatedInstructionsDialog from "../../../components/RelatedInstructionsDialog";
import AutoComplete, {
  IAutoCompleteProps,
} from "../../../components/common/AutoComplete";
import { hasJwtPermission } from "../../../services/ServicesHelpers";

const formatContentTranslationSummary = (
  translation: ContentTranslationType,
) => {
  const contentContains =
    translation.contentContains || translation.contentExtract || "";

  if (translation.operationType === "REPLACE") {
    return `If content contains (${contentContains}) replace (${translation.replaceValue || ""}) with (${translation.replaceWithValue || ""})`;
  }

  if (translation.operationType === "FORMAT_DIGITS") {
    return "If content is digits ONLY";
  }

  return `Extract code if content contains (${contentContains})`;
};

const ContentTranslationPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [appliedFilters, setAppliedFilters] = useState<
    Params<ContentTranslationType & DateRange>
  >({
    search: "",
  });

  const { data: providersOptions = [] } = useProviderOptionQuery();
  const { data: routingPlanOptions = [] } = useRoutingPlansQuery();
  const { data: countriesOptions = [] } = useCountriesQuery();
  const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
  const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
  const [filters, setFilters] = useState<
    Params<ContentTranslationType & { countryId: number }>
  >({});

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  const dialogRef = useRef<ContentTranslationDialogRef>(null);
  const [relatedRow, setRelatedRow] = useState<ContentTranslationType | null>(
    null,
  );

  const handleOpenDialog = (
    mode: "add" | "edit" | "delete" | "history",
    id?: string,
    isClone = false,
  ) => {
    dialogRef.current?.openDialog(mode, id, isClone);
  };

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedRowIds([]);
  }, [filters]);

  const onRowSelect = (row: ContentTranslationType, checked: boolean) => {
    setSelectedRowIds((prevSelected) => {
      const newSelected = checked
        ? [...prevSelected, String(row.id)]
        : prevSelected.filter((id) => id !== String(row.id));
      return newSelected;
    });
  };
  const isRowSelected = (row: ContentTranslationType) =>
    selectedRowIds.includes(String(row.id));

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    setAppliedFilters((prev) => ({
      ...prev,
      fromDate: fromDate,
      toDate: toDate,
      routingPlanId: filters.routingPlanId,
      operatorId: filters.operatorId,
      providerId: filters.providerId,
    }));
    setSelectedRowIds([]);
  };

  const handleCountryChange = async (
    value: string | number | (string | number)[],
  ) => {
    const selectedCountry = Array.isArray(value) ? value[0] : value;

    if (selectedCountry) {
      try {
        const countryId = Number(selectedCountry);
        if (isNaN(countryId)) {
          throw new Error("Invalid country ID");
        }
        setIsOperatorDisabled(true);
        const ops = await loadOperatorsByCountry(countryId);
        setOperatorsByCountry(ops);
      } catch (error: any) {
        console.error("Error fetching operators for selected country:", error);
        setOperatorsByCountry([]);
      } finally {
        setIsOperatorDisabled(false);
      }
    } else {
      setOperatorsByCountry([]);
      setIsOperatorDisabled(false);
    }
  };

  const actions = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
      },
      isDisabled: !hasJwtPermission("GET_CONTENT_TRANSLATION"),
    },
    onCloneAction: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id), true);
      },
      isDisabled: !hasJwtPermission("POST_CONTENT_TRANSLATION"),
    },
    // onDelete: {
    //   handler: async (id: string | number) => {
    //     const stringId = String(id);
    //     console.log(`Delete action triggered for ID: ${stringId}`);
    //     handleOpenDialog("delete", stringId);
    //   },
    //   isDisabled: false,
    // },
    onViewHistory: {
      // isDisabled: true,
      handler: async (id: number | string) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_CONTENT_TRANSLATIONS_HISTORY"),
    },
    onCompareRelated: {
      tooltip: "Content Translations on the same route",
      handler: (row: ContentTranslationType) => setRelatedRow(row),
      isDisabled: (row: ContentTranslationType) =>
        !hasJwtPermission("GET_ALL_CONTENT_TRANSLATIONS") ||
        !row?.routingPlanId ||
        !row?.operatorId,
    },
  });

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Routing Plan",
      value: filters.routingPlanId || "",
      options: routingPlanOptions,
      clearable: true,
      placeholder: "Select Routing Plan",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedPlan = routingPlanOptions.find(
          (option) => option.value === value,
        );
        setFilters((prev) => ({
          ...prev,
          routingPlanId: String(selectedPlan?.value ?? ""),
        }));
      },
    },
    {
      label: "Country",
      value: filters.countryId ?? "",
      options: countriesOptions,
      placeholder: "Select Country",

      clearable: true,
      onChange: async (value: string | number | (string | number)[]) => {
        const selectedCountry = countriesOptions.find(
          (option) => option.value === value,
        );

        setFilters((prev) => ({
          ...prev,
          countryId: Number(selectedCountry?.value),
          operatorId: undefined,
        }));

        if (selectedCountry) {
          try {
            setIsOperatorDisabled(true);
            const operators = await loadOperatorsByCountry(
              Number(selectedCountry.value),
            );
            setOperatorsByCountry(operators);
          } catch (error: any) {
            ToastNotification.error(error.message);

            setOperatorsByCountry([]);
          } finally {
            setIsOperatorDisabled(false);
          }
        }
      },
    },
    {
      label: "Operator",
      value: filters.operatorId ?? "",
      options: operatorsByCountry,
      disabled: isOperatorDisabled,

      clearable: true,
      placeholder: "Select Operator",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedOperator = operatorsByCountry.find(
          (option) => option.value === value,
        );

        setFilters((prev) => ({
          ...prev,
          operatorId: Number(selectedOperator?.value),
        }));
      },
    },

    {
      label: "Provider",
      value: filters.providerId ?? "",
      options: providersOptions,
      clearable: true,
      placeholder: "Select Provider",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedProvider = providersOptions.find(
          (option) => option.value === value,
        );
        setFilters((prev) => ({
          ...prev,
          providerId: String(selectedProvider?.value ?? ""),
        }));
      },
    },
  ];

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      permission: "GET_ALL_CONTENT_TRANSLATIONS",
    },
  ];

  const fields = useMemo<IField<ContentTranslationType>[]>(
    () => [
      { key: "name", header: "Name" },
      {
        key: "routingPlanId",
        header: "Routing Plan",
        transform: async (t) => {
          if (!t.routingPlanId) return "-";
          try {
            const rp = await queryClient.fetchQuery({
              queryKey: ["routingPlan", t.routingPlanId],
              queryFn: () => fetchRoutingPlan(t.routingPlanId),
            });
            return rp?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "providerId",
        header: "Provider",
        transform: async (t) => {
          if (!t.providerId) return "-";
          try {
            const p = await queryClient.fetchQuery({
              queryKey: ["provider", t.providerId],
              queryFn: () => fetchProvider(t.providerId),
            });
            return p?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "Country",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const op = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            if (!op?.countryId) return "-";
            const country = await queryClient.fetchQuery({
              queryKey: ["country", op.countryId],
              queryFn: () => fetchCountry(op.countryId),
            });
            return country?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "Operator",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const op = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            return op?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operationType",
        header: "Condition",
        transform: async (t) => formatContentTranslationSummary(t),
      },
      { key: "createdDate", header: "Created Date" },
      { key: "createdBy", header: "User" },
    ],
    [queryClient],
  );

  const providerNameField = useCallback(
    async (providerId?: string) => {
      if (!providerId) return "-";
      try {
        const p = await queryClient.fetchQuery({
          queryKey: ["provider", providerId],
          queryFn: () => fetchProvider(providerId),
        });
        return p?.name ?? "-";
      } catch {
        return "-";
      }
    },
    [queryClient],
  );

  // Routing plan / country / operator are identical for every row of this view,
  // so only the fields that can actually differ are listed.
  const relatedFields = useMemo<IField<ContentTranslationType>[]>(
    () => [
      { key: "name", header: "Name" },
      {
        key: "providerId",
        header: "Provider",
        transform: async (t) => providerNameField(t.providerId),
      },
      { key: "position", header: "Position" },
      {
        key: "operationType",
        header: "Condition",
        transform: async (t) => formatContentTranslationSummary(t),
      },
      { key: "contentContains", header: "Content Contains" },
      { key: "replaceValue", header: "Replace" },
      { key: "replaceWithValue", header: "Replace With" },
      { key: "codeLength", header: "Code Length" },
      { key: "isAlphanumeric", header: "Is AlphaNumeric" },
      { key: "separateDigitsBy", header: "Separate Digits By" },
      { key: "addBeforeExtractedCode", header: "Add Before Code" },
      { key: "addAfterExtractedCode", header: "Add After Code" },
      { key: "createdDate", header: "Created Date" },
      { key: "createdBy", header: "User" },
    ],
    [providerNameField],
  );

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <CustomLabel
        text="Content"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        marginTop={1}
        marginBottom={1}
      />
      <Box sx={{ mb: 3 }}>
        <SearchForm
          dateComponent={
            <CustomDateRangePicker
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
            />
          }
          autoCompletes={autoCompletes}
          buttons={buttons}
          rpdid="RPD"
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="Content List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginTop={1}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search ?? ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <GridAddIcon />,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.iconColor,
              permission: "POST_CONTENT_TRANSLATION",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              variant: "outlined" as ButtonProps["variant"],
              startIcon: <GridDeleteIcon />,
              onClick: () => handleOpenDialog("delete"),
              textColor: COLORS.iconColor,
              permission: "DELETE_CONTENT_TRANSLATION",
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,
              onClick: async () => {
                try {
                  await exportContentTranslation(appliedFilters);
                  ToastNotification.success("Export completed successfully!");
                } catch (error: any) {
                  ToastNotification.error(
                    error.message || "Export failed. Please try again.",
                  );
                }
              },
              showExportProgress: true,
              textColor: COLORS.iconColor,
              label: "Export",
              permission: "GET_ALL_CONTENT_TRANSLATIONS_CSV",
            },
          ]}
        />

        <CustomTable<ContentTranslationType>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useContentTranslationQuery}
          actions={actions}
          checkboxSelect={true}
          onRowSelect={onRowSelect}
          isRowSelected={isRowSelected}
          sortableFields={["name", "createdBy", "createdDate"]}
        />
      </Box>

      <ContentTranslationDialog
        ref={dialogRef}
        onRefresh={() => {
          setAppliedFilters((prev) => ({ ...prev }));
          queryClient.invalidateQueries({ queryKey: ["contentTranslation"] });
        }}
        selectedRowIds={selectedRowIds}
        onClearSelection={() => setSelectedRowIds([])}
        countriesOptions={countriesOptions}
        providersOptions={providersOptions}
        routingPlanOptions={routingPlanOptions}
        loadOperatorsByCountry={loadOperatorsByCountry}
        appliedFilters={appliedFilters}
      />

      <RelatedInstructionsDialog<ContentTranslationType>
        open={!!relatedRow}
        onClose={() => setRelatedRow(null)}
        row={relatedRow}
        title="Content Translations on the Same Route"
        fields={relatedFields}
        fetchFunc={useContentTranslationQuery}
      />
    </Box>
  );
};

interface DialogData extends Omit<ContentTranslationType, "operatorId"> {
  operatorId?: number | number[];
  countryId: number;
}

export interface ContentTranslationDialogRef {
  openDialog: (
    mode: "add" | "edit" | "delete" | "history",
    id?: string,
    isClone?: boolean,
  ) => void;
}

interface ContentTranslationDialogProps {
  onRefresh: () => void;
  countriesOptions: Option[];
  routingPlanOptions: Option[];
  providersOptions: Option[];
  selectedRowIds: string[];
  appliedFilters: Params<ContentTranslationType>;

  onClearSelection: () => void;
  loadOperatorsByCountry: (countryId: number) => Promise<Option[]>;
}

const ContentTranslationDialog = forwardRef<
  ContentTranslationDialogRef,
  ContentTranslationDialogProps
>(
  (
    {
      onRefresh,
      countriesOptions,
      routingPlanOptions,
      providersOptions,
      appliedFilters,
      loadOperatorsByCountry,
      selectedRowIds,
      onClearSelection,
    },
    ref,
  ) => {
    const queryClient = useQueryClient();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<
      "add" | "edit" | "delete" | "history"
    >("add");
    const [isCloneMode, setIsCloneMode] = useState(false);
    const [selectedId, setSelectedId] = useState<string | undefined>();
    const [dialogData, setDialogData] = useState<
      Partial<
        DialogData & {
          separateDigitsBy?: string;
          addBeforeExtractedCode?: string;
          addAfterExtractedCode?: string;
          contentExtract?: string;
        }
      >
    >({});
    const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
    const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [result, setResult] = useState<string>("");
    const [content, setContent] = useState<string>("");
    const [, setValue] = useState<string>("");
    const [selectedRadio, setSelectedRadio] = useState<string | null>(null);

    const validateField = (field: string, value: string) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: !value,
      }));
    };

    useImperativeHandle(ref, () => ({
      openDialog: (mode, id, isClone = false) => {
        setDialogMode(isClone ? "add" : mode);
        setSelectedId(isClone ? undefined : id);
        setIsCloneMode(isClone);
        setDialogOpen(true);
        openDialog(mode, id, isClone);
      },
    }));

    useEffect(() => {
      const requiredFields = [
        "routingPlanId",
        "countryId",
        "operatorId",
        "providerId",
        "name",
      ];

      const isAnyFieldEmpty = requiredFields.some((field) => {
        const value = dialogData[field as keyof typeof dialogData];
        if (Array.isArray(value)) return value.length === 0;
        return value === undefined || value === null || value === "";
      });

      setIsSaveDisabled(isAnyFieldEmpty);
    }, [dialogData]);

    const resetDialogData = () => {
      setDialogData({});
      setOperatorsByCountry([]);
      setIsSaveDisabled(true);
      setDialogOpen(false);
      setContent("");
      setResult("");
      setSelectedRadio(null);
      setIsOperatorDisabled(true);
      setIsCloneMode(false);
      onRefresh();
    };

    const handleCountryChange = async (
      value: string | number | (string | number)[],
    ) => {
      const selectedCountry = Array.isArray(value) ? value[0] : value;
      setDialogData((prev) => ({
        ...prev,
        countryId: Number(selectedCountry),
        operatorId: undefined,
      }));

      if (selectedCountry) {
        try {
          const countryId = Number(selectedCountry);
          if (isNaN(countryId)) {
            throw new Error("Invalid country ID");
          }

          setIsOperatorDisabled(false);
          const operators = await loadOperatorsByCountry(countryId);
          setOperatorsByCountry(operators);
        } catch (error: any) {
          ToastNotification.error(
            "Error fetching operators for selected country.",
          );
          setOperatorsByCountry([]);
        }
      } else {
        setOperatorsByCountry([]);
        setIsOperatorDisabled(true);
      }
    };

    const validateForm = () => {
      const errors = {
        name: !dialogData.name,
        routingPlanId: !dialogData.routingPlanId,
        operatorId: !dialogData.operatorId,
        providerId: !dialogData.providerId,
        position: !dialogData.position,
      };
      setIsSaveDisabled(Object.values(errors).some(Boolean));
    };

    useEffect(validateForm, [dialogData]);

    const openDialog = async (
      mode: "add" | "edit" | "delete" | "history",
      id?: string,
      isClone = false,
    ) => {
      setDialogMode(isClone ? "add" : mode);

      try {
        if (mode === "edit" || isClone) {
          if (!id) {
            console.warn("ID is required to edit a Content.");
            return;
          }

          const ContentData = await getContentTranslationById(id);

          if (ContentData.operationType === "FORMAT_DIGITS") {
            setSelectedRadio("formatDigits");
          } else if (ContentData.contentExtract) {
            setSelectedRadio("contentExtract");
          } else {
            setSelectedRadio("contentContains");
          }

          // A clone keeps every value of the source route, only the name has to
          // be re-entered because it must stay unique.
          let countryId: number | undefined;

          if (ContentData.operatorId) {
            const { countryId: selectedCountry } = await fetchOperator(
              ContentData.operatorId,
            );

            if (selectedCountry) {
              countryId = Number(selectedCountry);
              setIsOperatorDisabled(true);
              const operators = await loadOperatorsByCountry(countryId);
              setOperatorsByCountry(operators);
              setIsOperatorDisabled(false);
            }
          }

          const sourceOperatorId = Array.isArray(ContentData.operatorId)
            ? ContentData.operatorId[0]
            : ContentData.operatorId;

          setDialogData({
            ...ContentData,
            ...(isClone ? { id: undefined, version: undefined, name: "" } : {}),
            countryId: countryId ?? "",
            operatorId:
              isClone && sourceOperatorId != null
                ? [Number(sourceOperatorId)]
                : sourceOperatorId,
          });
        } else if (mode === "delete" && id) {
          setDialogData({ id: Number(id) });
        } else {
          setSelectedRadio("contentContains");
          setDialogData({
            name: "",
            effectiveDate: new Date(),
          });
        }

        setDialogOpen(true);
      } catch (error: any) {
        console.error(`Error fetching Content data for ${mode} mode:`, error);
      }
    };

    const addContentTranslation = async (
      data: Partial<
        DialogData & {
          separateDigitsBy?: string;
          addBeforeExtractedCode?: string;
          addAfterExtractedCode?: string;
          contentExtract?: string;
        }
      >,
    ) => {
      try {
        const { operatorId, ...rest } = data;

        const commonData: Partial<ContentTranslationType> = {
          ...rest,
          contentContains: data.contentExtract || data.contentContains,
          type: "CONTENT_TRANSLATION",
          isDeleted: false,
        };

        if (Array.isArray(operatorId) && operatorId.length >= 1) {
          const payloads: Partial<ContentTranslationType>[] = operatorId.map(
            (id, index) => ({
              ...commonData,
              operatorId: id,
              name: data.name ? `${data.name}_${index + 1}` : undefined,
            }),
          );
          await ContentTranslationCreateAllApi(payloads);
        }

        onRefresh();
        resetDialogData();
        ToastNotification.success("Content added successfully!");
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const editContentTranslation = async (
      data: Partial<
        DialogData & {
          separateDigitsBy?: string;
          addBeforeExtractedCode?: string;
          addAfterExtractedCode?: string;
          contentExtract?: string;
        }
      >,
    ) => {
      try {
        const id = data.id ? String(data.id) : "";
        if (!id) {
          ToastNotification.warning("Content ID is missing.");
          return;
        }

        const { operatorId, ...rest } = data;
        const payload: Partial<ContentTranslationType> = {
          ...rest,
          type: "CONTENT_TRANSLATION",
          isDeleted: false,
          operatorId: Array.isArray(operatorId) ? operatorId[0] : operatorId,
        };

        await editContentTranslationApi(id, payload);

        console.log("Content updated successfully");
        onRefresh();
        setDialogOpen(false);
        ToastNotification.success("Content updated successfully!");
      } catch (error: any) {
        ToastNotification.error(error.message);
      }
    };

    const deleteContentTranslation = async () => {
      try {
        await deleteContentTranslationApi(selectedRowIds);
        ToastNotification.success(
          "Selected content translation deleted successfully!",
        );
        onClearSelection();
        onRefresh();
        setDialogOpen(false);
      } catch {
        ToastNotification.error("Error deleting Content Translation.");
      }
    };

    const extractFirstNumericCode = (
      input: string,
      minSize: number,
      maxSize: number,
    ): string => {
      let firstCode: string[] = [];
      for (let i = 0; i < input.length; i++) {
        const c = input[i];
        if (/[A-Za-z0-9]/.test(c)) {
          if (Number.isInteger(Number(c))) {
            firstCode.push(c);
          }
          if (i == input.length - 1 || !Number.isInteger(Number(c))) {
            if (minSize <= firstCode.length && firstCode.length <= maxSize) {
              return firstCode.join("");
            } else {
              firstCode = [];
            }
          }
        }
      }
      if (firstCode.length) {
        return firstCode.join("");
      }
      return input;
    };

    const extractFirstAlphaNumericCode = (
      input: string,
      minSize: number,
      maxSize: number,
    ): string => {
      const words = input.split(" ");
      for (const word of words) {
        if (isValidAlphaNumeric(word, minSize, maxSize)) {
          return word;
        }
      }
      return input;
    };

    const isValidAlphaNumeric = (
      input: string,
      minSize: number,
      maxSize: number,
    ): boolean => {
      if (minSize > input.length) return false;
      if (maxSize < input.length) return false;
      return (
        /^[0-9A-Za-z]+$/.test(input) &&
        /[0-9]/.test(input) &&
        /[a-zA-Z]/.test(input)
      );
    };

    const handleTestClick = () => {
      if (selectedRadio === "formatDigits") {
        if (!content.trim()) {
          setResult("Error: Content field is empty.");
          ToastNotification.success("Testing completed. Result updated.");
          return;
        }

        if (!/^\d+$/.test(content.trim())) {
          setResult("Error: Content is not digits only.");
          ToastNotification.success("Testing completed. Result updated.");
          return;
        }

        let resultDigits = content.trim();

        if (dialogData.separateDigitsBy) {
          resultDigits = resultDigits
            .split("")
            .join(dialogData.separateDigitsBy);
        }

        if (dialogData.addBeforeExtractedCode) {
          resultDigits = dialogData.addBeforeExtractedCode + resultDigits;
        }

        if (dialogData.addAfterExtractedCode) {
          resultDigits = resultDigits + dialogData.addAfterExtractedCode;
        }

        setResult(resultDigits);
        ToastNotification.success("Testing completed. Result updated.");
        return;
      }

      if (selectedRadio === "contentReplace") {
        const searchTerm = dialogData.replaceValue;
        const replaceWith = dialogData.replaceWithValue;

        if (!content.trim()) {
          const msg = "Error: Content field is empty.";
          setResult(msg);
          ToastNotification.success("Testing completed. Result updated.");
          return;
        }
        if (!searchTerm) {
          const msg = "Error: No search term specified for replace.";
          setResult(msg);
          ToastNotification.success("Testing completed. Result updated.");
          return;
        }

        const replaced = content.split(searchTerm).join(replaceWith ?? "");
        setResult(replaced);
        ToastNotification.success(
          "Testing completed. Replace operation applied.",
        );
        return;
      }

      let testResult = content;

      if (content.trim() === "") {
        testResult = "Error: Content field is empty.";
        setResult(testResult);
        ToastNotification.success("Testing completed. Result updated.");
        return;
      }

      if (content.trim() === "") {
        testResult = "Error: Content field is empty.";
        setResult(testResult);
        ToastNotification.success("Testing completed. Result updated.");
        return;
      }

      const searchTerm =
        selectedRadio === "contentExtract"
          ? dialogData.contentExtract
          : dialogData.contentContains;

      if (!searchTerm) {
        testResult = "Error: No search term specified.";
        setResult(testResult);
        ToastNotification.success("Testing completed. Result updated.");
        return;
      }

      if (!content.toLowerCase().includes(searchTerm.toLowerCase())) {
        testResult = `No match found for: ${searchTerm}`;
        setResult(testResult);
        ToastNotification.success("Testing completed. Result updated.");
        return;
      }

      const termIndex = content.toLowerCase().indexOf(searchTerm.toLowerCase());
      if (termIndex === -1) {
        testResult = `No match found for: ${searchTerm}`;
        setResult(testResult);
        ToastNotification.success("Testing completed. Result updated.");
        return;
      }

      const startIndex = Math.max(0, termIndex - 50);
      const endIndex = Math.min(
        content.length,
        termIndex + searchTerm.length + 50,
      );
      const contextText = content.substring(startIndex, endIndex);

      const codeLength = dialogData.codeLength || 4;
      const isAlphaNumeric = dialogData.isAlphanumeric || false;

      const minSize = dialogData.codeLength ? codeLength : 4;
      const maxSize = dialogData.codeLength ? codeLength : 9;

      let extractedCode: string;
      if (isAlphaNumeric) {
        extractedCode = extractFirstAlphaNumericCode(
          contextText,
          minSize,
          maxSize,
        );
      } else {
        extractedCode = extractFirstNumericCode(contextText, minSize, maxSize);
      }

      if (extractedCode === contextText) {
        testResult = content;
        setResult(testResult);
        ToastNotification.success(
          "Testing completed. No translation applied - original content preserved.",
        );
        return;
      }

      if (extractedCode && extractedCode !== contextText) {
        let resultDigits = extractedCode;

        if (dialogData.separateDigitsBy) {
          resultDigits = resultDigits
            .split("")
            .join(dialogData.separateDigitsBy);
        }

        if (dialogData.addBeforeExtractedCode) {
          resultDigits = dialogData.addBeforeExtractedCode + resultDigits;
        }

        if (dialogData.addAfterExtractedCode) {
          resultDigits = resultDigits + dialogData.addAfterExtractedCode;
        }

        testResult = resultDigits;
      } else if (selectedRadio === "contentContains") {
        testResult = `Match found for: ${searchTerm}`;
      } else {
        testResult = extractedCode || `Match found for: ${searchTerm}`;
      }

      setResult(testResult);
      ToastNotification.success("Testing completed. Result updated.");
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setDialogData((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    const handleRadioChange = (type: string) => {
      setSelectedRadio(type);
    };

    const fieldHistory = useMemo<IField<ContentTranslationType>[]>(
      () => [
        { key: "name", header: "Name" },
        {
          key: "routingPlanId",
          header: "Routing Plan",
          transform: async (t) => {
            if (!t.routingPlanId) return "-";
            try {
              const rp = await queryClient.fetchQuery({
                queryKey: ["routingPlan", t.routingPlanId],
                queryFn: () => fetchRoutingPlan(t.routingPlanId),
              });
              return rp?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "providerId",
          header: "Provider",
          transform: async (t) => {
            if (!t.providerId) return "-";
            try {
              const p = await queryClient.fetchQuery({
                queryKey: ["provider", t.providerId],
                queryFn: () => fetchProvider(t.providerId),
              });
              return p?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "operatorId",
          header: "Country",
          transform: async (t) => {
            if (!t.operatorId) return "-";
            try {
              const op = await queryClient.fetchQuery({
                queryKey: ["operator", t.operatorId],
                queryFn: () => fetchOperator(t.operatorId),
              });
              if (!op?.countryId) return "-";
              const country = await queryClient.fetchQuery({
                queryKey: ["country", op.countryId],
                queryFn: () => fetchCountry(op.countryId),
              });
              return country?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "operatorId",
          header: "Operator",
          transform: async (t) => {
            if (!t.operatorId) return "-";
            try {
              const op = await queryClient.fetchQuery({
                queryKey: ["operator", t.operatorId],
                queryFn: () => fetchOperator(t.operatorId),
              });
              return op?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "operationType",
          header: "Condition",
          transform: async (t) => formatContentTranslationSummary(t),
        },
        { key: "createdDate", header: "Created Date" },
        { key: "createdBy", header: "User" },
        { key: "historyTimestamp", header: "Date Modified" },
        { key: "createdBy", header: "Created By" },
        { key: "updatedBy", header: "Updated By" },
      ],
      [queryClient],
    );

    return (
      <>
        <CustomDialog
          open={dialogOpen}
          onClose={resetDialogData}
          mode={dialogMode}
          title={
            dialogMode === "history"
              ? "History"
              : dialogMode === "delete"
                ? "Delete Content Translation"
                : dialogMode === "edit"
                  ? "Edit Content Translation"
                  : isCloneMode
                    ? "Clone Content Translation"
                    : "Add New Content Translation"
          }
          fields={
            dialogMode === "history"
              ? [
                  {
                    type: "custom",
                    component: (
                      <History
                        filters={
                          appliedFilters as Params<ContentTranslationType>
                        }
                        fields={fieldHistory}
                        fetchFunc={({ size = 10 }) =>
                          fetchContentHistory(Number(selectedId), {
                            size,
                          })
                        }
                      />
                    ),
                  },
                ]
              : dialogMode === "delete"
                ? [
                    {
                      type: "custom",
                      component: (
                        <Box>
                          <p>
                            Are you sure you want to delete this Content
                            Translation?
                          </p>
                        </Box>
                      ),
                    },
                  ]
                : [
                    {
                      type: "text",
                      label: "Content Translation Name",
                      bgColor: COLORS.WhiteBackground,
                      highlightLabelStar: true,
                      value: dialogData.name || "",
                      validateOnChange: true,
                      onValidation: (isError) => {
                        setErrors((prevErrors) => ({
                          ...prevErrors,
                          name: isError,
                        }));
                      },
                      onChange: (value) => {
                        const trimmedValue = String(value).trimStart();
                        setDialogData((prev) => ({
                          ...prev,
                          name: trimmedValue,
                        }));
                      },
                      placeholder: "Name",
                      error: !!errors.name,
                    },
                    {
                      type: "custom",
                      component: (
                        <AutoComplete
                          label="Routing Plan"
                          value={dialogData.routingPlanId ?? ""}
                          options={routingPlanOptions}
                          onChange={(
                            value: string | number | (string | number)[],
                          ) => {
                            setDialogData((prev) => ({
                              ...prev,
                              routingPlanId: String(value),
                            }));
                            validateField("routingPlan", String(value));
                          }}
                          clearable={true}
                          disablePortal={false}
                          error={!!errors.routingPlan}
                          placeholder="Select Routing Plan"
                          highlightLabelStar={true}
                          helperText={
                            errors.routingPlan ? "Routing Plan is required" : ""
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: COLORS.WhiteBackground,
                            },
                          }}
                        />
                      ),
                    },
                    {
                      type: "custom",
                      component: (
                        <AutoComplete
                          label="Provider"
                          value={dialogData.providerId ?? ""}
                          options={providersOptions}
                          onChange={(
                            value: string | number | (string | number)[],
                          ) => {
                            setDialogData((prev) => ({
                              ...prev,
                              providerId: String(value),
                            }));
                            validateField("providerId", String(value));
                          }}
                          clearable={true}
                          disablePortal={false}
                          error={!!errors.providerId}
                          placeholder="Select Provider"
                          highlightLabelStar={true}
                          helperText={
                            errors.providerId ? "Provider is required" : ""
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: COLORS.WhiteBackground,
                            },
                          }}
                        />
                      ),
                    },
                    {
                      type: "custom",
                      component: (
                        <AutoComplete
                          label="Country"
                          value={dialogData.countryId ?? ""}
                          options={countriesOptions}
                          onChange={handleCountryChange}
                          clearable={true}
                          disablePortal={false}
                          error={!!errors.country}
                          placeholder="Select Country"
                          highlightLabelStar={true}
                          helperText={
                            errors.country ? "Country is required" : ""
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: COLORS.WhiteBackground,
                            },
                          }}
                        />
                      ),
                    },
                    {
                      type: "custom",
                      component: (
                        <AutoComplete
                          label="Operator"
                          multiple={dialogMode === "add"}
                          selectAll={dialogMode === "add"}
                          hideChips={dialogMode === "add"}
                          value={
                            dialogMode === "add"
                              ? Array.isArray(dialogData.operatorId)
                                ? dialogData.operatorId.map(String)
                                : []
                              : dialogData.operatorId !== undefined &&
                                  dialogData.operatorId !== null
                                ? String(
                                    Array.isArray(dialogData.operatorId)
                                      ? dialogData.operatorId[0]
                                      : dialogData.operatorId,
                                  )
                                : ""
                          }
                          options={operatorsByCountry}
                          onChange={(
                            value: string | number | (string | number)[],
                          ) => {
                            if (dialogMode === "add") {
                              const valueArray: number[] = Array.isArray(value)
                                ? value.map((v) => Number(v))
                                : value
                                  ? [Number(value)]
                                  : [];

                              setDialogData((prev) => ({
                                ...prev,
                                operatorId: valueArray,
                              }));
                            } else {
                              setDialogData((prev) => ({
                                ...prev,
                                operatorId:
                                  value !== "" && value !== undefined
                                    ? Number(
                                        Array.isArray(value) ? value[0] : value,
                                      )
                                    : undefined,
                              }));
                            }
                          }}
                          clearable={true}
                          disablePortal={false}
                          error={!!errors.operatorId}
                          placeholder="Select Operator"
                          highlightLabelStar={true}
                          helperText={
                            errors.operatorId ? "Operator is required" : ""
                          }
                          disabled={isOperatorDisabled}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: COLORS.WhiteBackground,
                            },
                          }}
                        />
                      ),
                    },
                    {
                      type: "select",
                      label: "Position",
                      bgColor: COLORS.WhiteBackground,
                      highlightLabelStar: true,
                      value: dialogData.position || "",
                      options: [
                        {
                          label: "Apply Before Sub & TPS Routing",
                          value: "BEFORE_SUB_ROUTING",
                        },
                        {
                          label: "Apply After Sub & TPS Routing",
                          value: "AFTER_SUB_ROUTING",
                        },
                      ],
                      onChange: (
                        value: string | number | (string | number)[],
                      ) => {
                        setDialogData((prev) => ({
                          ...prev,
                          position: String(value) as
                            | "BEFORE_TPS_ROUTING"
                            | "AFTER_TPS_ROUTING",
                        }));
                      },
                      placeholder: "Select Position",
                      error: !!errors.position,
                      helperText: errors.position ? "Position is required" : "",
                    },
                    {
                      type: "custom",
                      label: "Conditions",
                      highlightLabelStar: true,
                      component: (
                        <CombinedSection
                          selectedRadio={selectedRadio}
                          onRadioChange={handleRadioChange}
                          onInputChange={handleInputChange}
                          setDialogData={setDialogData}
                          dialogData={dialogData}
                          checkboxes={[
                            {
                              label: "Is AlphaNumeric ",
                              value: "isAlphanumeric",
                            },
                            {
                              label: "Code length ",
                              value: "codeLength",
                              defaultValue: "9",
                            },
                            {
                              label: "Separate digits by",
                              value: "separateDigitsBy",
                            },
                            {
                              label: "Add before extracted code",
                              value: "addBeforeExtractedCode",
                            },
                            {
                              label: "Add after extracted code",
                              value: "addAfterExtractedCode",
                            },
                          ]}
                        />
                      ),
                    },
                    {
                      type: "custom",
                      label: "Test",
                      highlightLabelStar: true,
                      component: (
                        <TestingSection
                          content={content}
                          setContent={setContent}
                          value={result}
                          setValue={setValue}
                          handleTestClick={handleTestClick}
                        />
                      ),
                    },
                  ]
          }
          buttons={
            dialogMode === "history"
              ? []
              : [
                  dialogMode !== "delete"
                    ? {
                        label: "Save",
                        variant: "contained",
                        bgColor: COLORS.GOLD,
                        borderRadius: "12px",
                        onClick: async () => {
                          if (dialogMode === "edit") {
                            await editContentTranslation(dialogData);
                          } else {
                            await addContentTranslation(dialogData);
                          }
                        },
                        disabled:
                          isSaveDisabled ||
                          (dialogMode === "edit"
                            ? !hasJwtPermission("PUT_CONTENT_TRANSLATION")
                            : !hasJwtPermission("POST_CONTENT_TRANSLATION")),
                      }
                    : {
                        label: "Delete",
                        variant: "contained",
                        bgColor: COLORS.GOLD,
                        textColor: COLORS.iconColor,
                        borderRadius: "12px",
                        onClick: deleteContentTranslation,
                        disabled: !hasJwtPermission(
                          "DELETE_CONTENT_TRANSLATION",
                        ),
                      },
                ]
          }
        />
      </>
    );
  },
);
export default ContentTranslationPage;
